import { Text } from '@dixa/lib-shared-habla';
import {
  PendingAction,
  UseSuggestionActions,
} from 'articleSuggestions/hooks/useSuggestionActions';
import { Suggestion } from 'articleSuggestions/utils/toSuggestion';
import {
  AiDraftType,
  useArticleTranslationPublishMutation,
  useKnowledgeGapDraftCreateMutation,
  useKnowledgeGapDraftUpsertMutation,
} from 'lib-knowledge-management-common/graphql';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { routeGenerator } from 'lib-knowledge-utils/routes';
import { useState } from 'react';

export function useTestDraftActions({
  suggestion,
  onRefile,
  onRemoved,
}: {
  suggestion: Suggestion;
  onRefile: (target: {
    collectionId?: string | null;
    categoryId?: string | null;
  }) => void;
  onRemoved: () => void;
}): UseSuggestionActions {
  const { successNotify, errorNotify } = useNotification();
  const [pendingAction, setPendingAction] = useState<PendingAction>();
  const [createArticle] = useKnowledgeGapDraftCreateMutation();
  const [upsertTranslation] = useKnowledgeGapDraftUpsertMutation();
  const [publishTranslation] = useArticleTranslationPublishMutation();

  const {
    type,
    title,
    htmlContent,
    languageId,
    collectionId,
    categoryId,
    forArticleId,
    conversations,
  } = suggestion;

  const missing =
    type === AiDraftType.ArticleUpdate
      ? []
      : [
          collectionId ? undefined : 'a collection',
          categoryId ? undefined : 'a category',
        ].filter((part): part is string => !!part);
  const missingFiling = missing.length ? missing.join(' and ') : undefined;
  const missingArticle = type === AiDraftType.ArticleUpdate && !forArticleId;

  const writeArticle = async (): Promise<string | undefined> => {
    if (type === AiDraftType.ArticleUpdate) {
      if (!forArticleId) return undefined;
      await upsertTranslation({
        variables: {
          articleId: forArticleId,
          languageId,
          title,
          contentHtml: htmlContent,
        },
      });
      return forArticleId;
    }

    if (!categoryId) return undefined;
    const { data } = await createArticle({
      variables: {
        parentId: categoryId,
        translations: [
          {
            languageId,
            title,
            contentHtml: htmlContent,
            aiAssisted: true,
            referencedConversationIds: conversations.map(({ csid }) => csid),
          },
        ],
      },
    });
    return data?.articleCreate?.id;
  };

  const save = async (alsoPublish: boolean) => {
    setPendingAction(alsoPublish ? 'publish' : 'approve');
    let articleId: string | undefined;

    try {
      articleId = await writeArticle();
      if (!articleId) throw new Error('The article could not be saved.');
      if (alsoPublish) {
        await publishTranslation({ variables: { articleId, languageId } });
      }

      successNotify({
        title: alsoPublish ? 'Article published' : 'Article saved',
        text: (
          <>
            {alsoPublish
              ? `“${title}” is live. `
              : `“${title}” is saved as a draft article. `}
            <ArticleLink articleId={articleId} suggestion={suggestion} />
          </>
        ),
        icon: 'check-circle',
        time: 10000,
      });
      onRemoved();
    } catch (error) {
      if (articleId) {
        // Already written, so it leaves the results: saving again would
        // create a duplicate.
        errorNotify({
          title: 'Article not published',
          text: (
            <>
              {`“${title}” is saved as a draft article, but it could not be published. `}
              <ArticleLink articleId={articleId} suggestion={suggestion} />
            </>
          ),
          time: 10000,
        });
        onRemoved();
      } else {
        errorNotify({ text: (error as Error)?.message });
      }
    } finally {
      setPendingAction(undefined);
    }
  };

  const canApprove = !missingFiling && !missingArticle;

  return {
    discard: onRemoved,
    publish: () => void save(true),
    approve: () => void save(false),
    improve: () => {},
    refile: onRefile,
    isImproving: false,
    isRefiling: false,
    pendingAction,
    isSaving: !!pendingAction,
    canApprove,
    missingFiling,
    isLocked: false,
    approveBlockedReason: missingFiling
      ? `Choose ${missingFiling} for this suggestion before it can be added or published.`
      : missingArticle
        ? 'This suggestion does not say which article it updates.'
        : undefined,
  };
}

function ArticleLink({
  articleId,
  suggestion,
}: {
  articleId: string;
  suggestion: Suggestion;
}) {
  return (
    <Text.ExternalLink
      href={routeGenerator.articleEdit({
        articleId,
        collectionId: suggestion.collectionId,
        languageId: suggestion.languageId,
      })}
      target="_blank"
      rel="noreferrer"
    >
      Open article
    </Text.ExternalLink>
  );
}
